import { resolveInferenceEngine, resolveInferenceOptions } from "../resolveInferenceEngine";
import { Liquid, Tokenizer, TokenKind } from "liquidjs";
import * as yup from "yup";
import { MAX_SYNOPSIS_WORDS, MAX_BRAINDUMP_WORDS } from "../constants";

export class PluginException extends Error {}

export const TYPES = ["document"];
export const MIN_NAME_LENGTH = 1;
export const MIN_DESCRIPTION_LENGTH = 5;

export const PluginStatus = {
  PUBLISHED: "PUBLISHED",
  UNLISTED: "UNLISTED",
};

export const PluginPromptVisibility = {
  HIDDEN: "HIDDEN",
  VISIBLE: "VISIBLE",
};

export const BLOCK_TYPES = {
  INPUT_DOCUMENT: "input_document",
  INFERENCE: "inference",
  CARD_OUTPUT: "card_output",
  INPUT_STORY_BIBLE: "input_story_bible",
  CLOSE_REQUEST: "close_request",
  API_INPUT: "api_input",
  AB_TEST: "ab_test",
  COST_CONTROLS: "cost_controls",
};

/**IMPORTANT:
 * DO NOT IMPORT THIS DIRECTLY IN THE SERVER
 * This should be imported using the enhanced version in./privateEngineData, which contains secret information not suitable for the client
 *
 * availableEngines — authoritative source of truth for plugin model output capacity.
 *
 * maxTokens: Maximum output tokens the provider accepts for this model.
 *            This value drives the editor slider, server-side yup validation, and the
 *            output-capacity clamp in inference.js. NOT total tokens (input + output).
 *
 * productCap: When true, the maxTokens value is a Sudowrite-imposed product cap rather
 *             than a provider-documented completion maximum. The parity test skips the
 *             "must match provider adapter" assertion for these engines and instead
 *             verifies the cap is reasonable (> 0, <= adapter maxTotalTokens).
 *
 * Adapter registries (OpenRouterOld.js, Gemini.js) also define maxTokens for their own
 * context-window calculations. Those values MUST be >= this value.
 * The engineParity.test.js file enforces this invariant.
 */
export const availableEngines = {
  "gpt-3.5-turbo": {
    provider: "gpt",
    description: "Latest ChatGPT model. Inexpensive, good at following instructions, may reject explicit content. • Works with up to ~3,000 words (input + output)",
    type: "chat",
    maxTokens: 4096,
    supportsNCompletions: true,
    creditUsageLevel: 2,
  },

  "gpt-3.5-turbo-16k": {
    provider: "gpt",
    description: "Like 3.5-turbo, but bigger! Good for chapter work and medium cost. • Works with up to ~12,000 words (input + output)",
    type: "chat",
    maxTokens: 4_096,
    supportsNCompletions: true,
    creditUsageLevel: 2,
  },
  "mistralai/mistral-small-3.2-24b-instruct": {
    provider: "openRouter",
    description: "An updated model from Mistral optimized for instruction following and repetition reduction.",
    type: "chat",
    maxTokens: 16_384,
  },
  // Removed by OpenRouter
  // "mistralai/mistral-medium": {
  //   provider: "openRouter",
  //   description: "Latest MistralAI model. Good at following instructions and medium cost. • Works with up to ~24,000 words (input + output)",
  //   type: "chat",
  //   maxTokens: 32768,
  // },

  "x-ai/grok-4.3": {
    provider: "openRouter",
    description: "xAI Grok 4.3. Strong reasoning and instruction following with a large context window. • Up to ~750,000 words (input + output)",
    type: "chat",
    maxTokens: 1_000_000,
    supportsNCompletions: false,
    creditUsageLevel: 3,
  },
  "x-ai/grok-4.7": {
    provider: "openRouter",
    description: "Grok 4.7 for detailed directions and creative writing.",
    type: "chat",
    maxTokens: 32_768,
    supportsNCompletions: false,
    creditUsageLevel: 3,
  },
  "z-ai/glm-5.3": {
    provider: "openRouter",
    description: "GLM 5.3 for detailed scene directions.",
    type: "chat",
    maxTokens: 131_072,
    supportsNCompletions: false,
    creditUsageLevel: 3,
  },
  "z-ai/glm-5.3-flash": {
    provider: "openRouter",
    description: "GLM 5.3 Flash for quick, economical drafts.",
    type: "chat",
    maxTokens: 131_072,
    supportsNCompletions: false,
    creditUsageLevel: 2,
  },
  "qwen/qwen3.8-max": {
    provider: "openRouter",
    description: "Qwen 3.8 Max for complex scenes and detailed directions.",
    type: "chat",
    maxTokens: 131_072,
    supportsNCompletions: false,
    creditUsageLevel: 3,
  },

  "o1-preview": {
    provider: "gpt",
    description: "The latest reasoning model from OpenAI. High intelligence and high cost. • Works with up to ~100,000 words input, 25,000 words output.",
    maxTokens: 32_768,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 4,
    hidden: true,
  },

  "o1-mini": {
    provider: "gpt",
    description: "The latest reasoning model from OpenAI. High intelligence and high cost. • Works with up to ~100,000 words input, 25,000 words output.",
    maxTokens: 32_768,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 4,
    hidden: true,
  },

  "gpt-4o": {
    provider: "gpt",
    description: "The hottest GPT-4 model. High intelligence and medium cost. • Works with up to ~90,000 words input, 3,000 words output.",
    maxTokens: 16_384,
    maxInputTokens: 128_000,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 2,
  },

  "gpt-4o-mini": {
    provider: "gpt",
    description: "The most advanced small GPT-4 model. Inexpensive and better than GPT 3.5 Turbo. • Works with up to ~90,000 words input, 3,000 words output.",
    maxTokens: 16_384,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 1,
  },
  "gpt-4o-2024-08-06": {
    provider: "gpt",
    description: "A version of the GPT-4o model from August 2024. High intelligence and medium cost. • Works with up to ~90,000 words input, 13,000 words output.",
    maxTokens: 16_384,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 2,
  },
  "gpt-4o-2024-11-20": {
    provider: "gpt",
    description: "The latest gpt-4o model from November 2024. High intelligence and medium cost. • Works with up to ~90,000 words input, 13,000 words output.",
    maxTokens: 16_384,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 2,
  },

  "gpt-4.1": {
    provider: "gpt",
    description: "The latest gpt-4.1 model from April 2025. High intelligence and medium cost. • Works with up to ~500,000 words input, 20,000 words output.",
    maxTokens: 32_768,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 3,
  },

  "gpt-4.1-mini": {
    provider: "gpt",
    description: "The latest gpt-4.1 model from April 2025. High intelligence and low cost. • Works with up to ~500,000 words input, 20,000 words output.",
    maxTokens: 32_768,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 2,
  },

  // GPT-5 per docs
  "gpt-5": {
    provider: "gpt",
    description: "OpenAI GPT-5 (Sep 30, 2024 cutoff). Large 400k context, 128k max output, reasoning token support.",
    maxTokens: 128_000,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 3,
  },
  "gpt-5-mini": {
    provider: "gpt",
    description: "GPT-5 Mini. Smaller, cheaper variant. Reasoning token support.",
    maxTokens: 128_000,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 2,
  },
  "gpt-5-nano": {
    provider: "gpt",
    description: "GPT-5 Nano. Fastest, most economical variant. Reasoning token support.",
    maxTokens: 128_000,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 1,
  },
  "gpt-5.4": {
    provider: "gpt",
    description: "OpenAI GPT-5.4 (March 2026). 1M context, 128k max output, reasoning token support.",
    maxTokens: 128_000,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 3,
  },
  "gpt-5.5": {
    provider: "gpt",
    description: "OpenAI GPT-5.5 (June 2026). 1M context, 128k max output, reasoning token support.",
    maxTokens: 128_000,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 4,
  },
  "gpt-6-astra": {
    provider: "gpt",
    description: "OpenAI's most capable model for complex scenes and detailed instructions. Higher credit use.",
    maxTokens: 128_000,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 5,
  },
  "gpt-6.1-sol": {
    provider: "gpt",
    description: "OpenAI's upgraded Sol model for detailed scenes and complex instructions.",
    maxTokens: 128_000,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 3,
  },
  "gpt-6-sol": {
    provider: "gpt",
    description: "OpenAI's versatile model for detailed scenes and complex instructions.",
    maxTokens: 128_000,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 3,
  },
  "gpt-6-luna": {
    provider: "gpt",
    description: "OpenAI's fast, affordable model for everyday writing.",
    maxTokens: 128_000,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 3,
  },
  "gpt-5.6-sol": {
    provider: "gpt",
    description: "OpenAI's flagship GPT-5.6 tier. 1M context, 128k max output, reasoning token support.",
    maxTokens: 128_000,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 4,
  },
  "gpt-5.6-terra": {
    provider: "gpt",
    description: "OpenAI's balanced GPT-5.6 tier. 1M context, 128k max output, reasoning token support.",
    maxTokens: 128_000,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 3,
  },
  "gpt-5.6-luna": {
    provider: "gpt",
    description: "OpenAI's fastest and most economical GPT-5.6 tier. 1M context, 128k max output, reasoning token support.",
    maxTokens: 128_000,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 3,
  },

  muse: {
    provider: "sudowrite",
    description:
      "The latest Muse model from Sudowrite, tuned to write highly coherent and creative literary-style prose. Can write complete short stories and whole chapters at a time. • Works with up to ~100,000 words input, 3,500 words output.",
    maxTokens: 32_768,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 3,
    hidden: true,
  },
  "ballad-1.1-write": {
    provider: "customModel",
    description: "Ballad 1.1 Write",
    maxTokens: 16_384,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 3,
    hidden: true,
  },
  "ballad-rewrite": {
    provider: "customModel",
    description: "Ballad Rewrite",
    maxTokens: 16_384,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 3,
    hidden: true,
  },
  "ballad-rewrite-v11": {
    provider: "customModel",
    description: "Ballad Rewrite V11",
    maxTokens: 16_384,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 3,
    hidden: true,
  },
  "ballad-1.1-draft": {
    provider: "customModel",
    description: "Ballad 1.1 Draft",
    maxTokens: 16_384,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 3,
    hidden: true,
  },
  muse1: {
    provider: "sudowrite",
    description:
      "The latest Muse model from Sudowrite, tuned to write highly coherent and creative literary-style prose. Can write complete short stories and whole chapters at a time. • Works with up to ~100,000 words input, 12,000 words output.",
    maxTokens: 4_096,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 3,
    hidden: true,
  },
  myVoice: {
    provider: "sudowrite",
    description:
      "The latest My Voice model from Sudowrite, tuned to write highly coherent and creative prose in a given style. Can write complete short stories and whole chapters at a time. • Works with up to ~100,000 words input, 12,000 words output.",
    maxTokens: 32_768,
    type: "chat",
    supportsNCompletions: true,
    creditUsageLevel: 3,
    hidden: true,
  },
  "mancer/weaver": {
    provider: "openRouter",
    description: "Uncensored model. Inexpensive and roughly as smart as GPT-3.5. • Works with up to ~6,000 words input, 400 words output.",
    type: "chat",
    maxTokens: 6_000,
    maxN: 1,
    creditUsageLevel: 2,
  },
  "gryphe/mythomax-l2-13b": {
    provider: "openRouter",
    description: "Uncensored model. Inexpensive and known for rich descriptions and roleplay. • Works with up to ~3,000 words (input + output)",
    type: "chat",
    maxTokens: 4096,
    creditUsageLevel: 2,
  },
  // Removed by OpenRouter
  // Leaving this as comment for now in case it comes back (this has happened before)
  // "alpindale/goliath-120b": {
  //   provider: "openRouter",
  //   description: "One of the largest open models. Medium cost. • Works with up to ~3,000 words (input + output)",
  //   type: "chat",
  //   maxTokens: 4096,
  //   creditUsageLevel: 3,
  // },
  // Removed by OpenRouter
  // "jondurbin/airoboros-l2-70b": {
  //   provider: "openRouter",
  //   description: "Inexpensive Llama 2 70B-based model. • Works with up to ~3,000 words (input + output)",
  //   type: "chat",
  //   maxTokens: 4096,
  //   creditUsageLevel: 2,
  // },
  "gemini-2.5-pro": {
    provider: "gemini",
    description:
      "Google's flagship text generation model. Designed to handle natural language tasks, multiturn text and code chat, and code generation. • Works with up to ~750k words (input + output)",
    type: "chat",
    maxTokens: 65536,
    creditUsageLevel: 3,
    defaultThinkingBudget: 2000,
  },
  "gemini-3.1-pro-preview": {
    provider: "gemini",
    description: "Google's Gemini 3.1 Pro — a reasoning-optimized upgrade to Gemini 3 Pro with extended context for fiction workflows. • Works with up to ~750k words (input + output)",
    type: "chat",
    maxTokens: 65536,
    creditUsageLevel: 5,
    defaultThinkingBudget: 2000,
  },

  "gemini-3-flash-preview": {
    provider: "gemini",
    description: "Google's Gemini 3 Flash with thinking tokens for good reasoning at lower cost. • Works with up to ~750k words (input + output)",
    type: "chat",
    maxTokens: 65536,
    creditUsageLevel: 2,
    defaultThinkingBudget: 2000,
  },
  "gemini-3.7-flash": {
    provider: "gemini",
    description: "Gemini 3.7 Flash for everyday drafts and detailed directions.",
    type: "chat",
    maxTokens: 65_536,
    creditUsageLevel: 2,
    defaultThinkingBudget: 2000,
  },
  "gemini-3.8-flash": {
    provider: "gemini",
    description: "Gemini 3.8 Flash for complex directions and quick drafts.",
    type: "chat",
    maxTokens: 65_536,
    creditUsageLevel: 2,
    defaultThinkingBudget: 2000,
  },
  "gemini-2.5-flash": {
    provider: "gemini",
    description: "Gemini 2.5 Flash with reasoning. Optimized for cost and speed while. • Works with up to 400,000 words (input + output).",
    type: "chat",
    maxTokens: 65_536,
    creditUsageLevel: 2,
    defaultThinkingBudget: 2000,
  },
  "deepseek/deepseek-r1": {
    provider: "openRouter",
    description:
      "High reasoning model that is as good quality as o1 at a fraction of the cost. • Works with up to ~160,000 words (input + output). (Note: that this model are currently in flux and performance and adherance to parameters may vary. It can only output one card at a time.)",
    type: "chat",
    maxTokens: 16_000,
    creditUsageLevel: 2,
  },
  "deepseek/deepseek-chat": {
    provider: "openRouter",
    description: "DeepSeek-V3 is the latest model from the DeepSeek team. • Works with up to ~64,000 words (input + output)",
    type: "chat",
    maxTokens: 16_400,
    creditUsageLevel: 1,
  },
  "deepseek/deepseek-chat-v3.1": {
    provider: "openRouter",
    description: "DeepSeek V3.1 delivers stronger reasoning and expanded 160K context compared to V3. • Works with up to ~160,000 words (input + output)",
    type: "chat",
    maxTokens: 32_768,
    creditUsageLevel: 2,
  },
  "deepseek/deepseek-v4-pro": {
    provider: "openRouter",
    description: "DeepSeek V4 Pro is a large-scale MoE model excelling at creative writing with 1M context. • Works with up to ~384,000 words (input + output)",
    type: "chat",
    maxTokens: 384_000,
    creditUsageLevel: 2,
  },
  // Removed by OpenRouter
  // "mistralai/mistral-large-2411": {
  //   provider: "openRouter",
  //   description: "Mistral Large 2 2411 is an update of Mistral Large. Long context and good at following directions. • Works with up to ~256,000 words (input + output)",
  //   type: "chat",
  //   maxTokens: 4_000,
  //   creditUsageLevel: 3,
  // },
  // Removed by OpenRouter; retained as a reversible unregistration.
  // "mistralai/mistral-large-2512": {
  //   provider: "openRouter",
  //   description: "Mistral Large 3 is Mistral's strongest open model. Long context and good at following directions. • Works with up to ~256,000 words (input + output)",
  //   type: "chat",
  //   maxTokens: 128_000, // Product cap: OpenRouter reports no explicit completion maximum; capped at half context window for safety
  //   productCap: true,
  //   creditUsageLevel: 2,
  // },
  "z-ai/glm-5.2": {
    provider: "openRouter",
    description: "GLM 5.2 by Z.ai. Flexible prose generation with strong instruction following for detailed scenes and long stories.",
    type: "chat",
    maxTokens: 64_000,
    maxN: 1,
    supportsNCompletions: false,
    creditUsageLevel: 2,
  },
  "moonshotai/kimi-k2": {
    provider: "openRouter",
    description: "Kimi K2 by Moonshot AI. Advanced MoE model with strong reasoning capabilities and tool usage. • Works with up to ~96,000 words (input + output)",
    type: "chat",
    maxTokens: 100_352,
    creditUsageLevel: 2,
  },
  "moonshotai/kimi-k2-thinking": {
    provider: "openRouter",
    description: "Kimi K2 Thinking by Moonshot AI. Extended reasoning variant with persistent thinking. • Works with up to ~175,000 words (input + output)",
    type: "chat",
    maxTokens: 100_352,
    creditUsageLevel: 3,
  },
  "moonshotai/kimi-k3": {
    provider: "openRouter",
    description: "Kimi K3 is Moonshot AI's newest open model, with strong reasoning and instruction following.",
    type: "chat",
    maxTokens: 1_048_576,
    maxN: 1,
    supportsNCompletions: false,
    creditUsageLevel: 4,
  },

  "claude-haiku-5-5": {
    provider: "claude",
    type: "chat",
    maxTokens: 128_000,
    description: "A fast, low-cost model for drafting and following your directions.",
    creditUsageLevel: 1,
    disallowsTemperature: true,
  },
  "claude-haiku-4-5-20251001": {
    provider: "claude",
    type: "chat",
    maxTokens: 64_000,
    description: "Claude Haiku 4.5 pairs fast responses with improved reasoning. • Works with up to ~150,000 words (input + output)",
    creditUsageLevel: 2,
  },
  "claude-sonnet-4-5-20250929": {
    provider: "claude",
    description: "The latest Claude Sonnet model. High intelligence and medium cost. • Works with up to ~150,000 words input, 20,000 words output.",
    maxTokens: 64_000,
    type: "chat",
    creditUsageLevel: 3,
  },
  "claude-sonnet-4-6": {
    provider: "claude",
    description: "The newest Claude Sonnet model. High intelligence and medium cost. • Works with up to ~150,000 words input, 64,000 words output.",
    maxTokens: 64_000,
    type: "chat",
    creditUsageLevel: 3,
  },
  "claude-sonnet-5": {
    provider: "claude",
    description: "Anthropic's most capable Sonnet model. High intelligence and medium cost. • Works with up to ~150,000 words input, 64,000 words output.",
    maxTokens: 64_000,
    type: "chat",
    creditUsageLevel: 3,
    disallowsTemperature: true,
  },
  "claude-sonnet-5-5": {
    provider: "claude",
    description: "Anthropic's latest Sonnet model for expressive prose, careful instruction following, and fast responses at a moderate cost.",
    maxTokens: 128_000,
    type: "chat",
    creditUsageLevel: 3,
    disallowsTemperature: true,
  },
  "claude-opus-4-1-20250805": {
    provider: "claude",
    description: "The newest version of Claude's most powerful model. High intelligence and high cost. • Works with up to ~150,000 words input, 20,000 words output.",
    maxTokens: 32_000,
    type: "chat",
    creditUsageLevel: 5,
  },
  "claude-opus-4-5-20251101": {
    provider: "claude",
    description: "Anthropic's newest Opus series release with stronger reasoning, improved coherence, and better cost efficiency. • Works with up to ~150,000 words, 20,000 words output.",
    maxTokens: 64_000,
    type: "chat",
    creditUsageLevel: 4,
  },
  "claude-opus-4-6": {
    provider: "claude",
    description: "Anthropic's most intelligent model with exceptional reasoning and prose quality, and 128K output. • Works with up to ~150,000 words, 20,000 words output.",
    maxTokens: 128_000,
    type: "chat",
    creditUsageLevel: 4,
  },
  "claude-opus-4-7": {
    provider: "claude",
    description: "Anthropic's most capable model with exceptional reasoning and prose quality. • Works with up to ~150,000 words input, 20,000 words output.",
    maxTokens: 128_000,
    type: "chat",
    creditUsageLevel: 4,
    disallowsTemperature: true,
  },
  "claude-opus-4-8": {
    provider: "claude",
    description: "Anthropic's most capable model with exceptional reasoning and prose quality. • Works with up to ~150,000 words input, 20,000 words output.",
    maxTokens: 128_000,
    type: "chat",
    creditUsageLevel: 4,
    disallowsTemperature: true,
  },
  "claude-opus-5": {
    provider: "claude",
    description: "Anthropic's newest Opus model with exceptional reasoning, instruction following, and prose quality. • Works with up to ~750,000 words input, 95,000 words output.",
    maxTokens: 128_000,
    type: "chat",
    creditUsageLevel: 4,
    disallowsTemperature: true,
  },
  "claude-opus-5-5": {
    provider: "claude",
    description: "Anthropic's newest Opus model with exceptional reasoning, instruction following, and prose quality. • Works with up to ~750,000 words input, 95,000 words output.",
    maxTokens: 128_000,
    type: "chat",
    creditUsageLevel: 2,
    disallowsTemperature: true,
    // Validated support for the per-call Bedrock adaptive/low opt-in, not a default.
    supportsBedrockAdaptiveThinkingEffort: true,
  },
  "claude-fable-5": {
    provider: "claude",
    description: "Anthropic's most intelligent model, best for complex reasoning, planning, and instruction-following. Prose quality is comparable to Opus. • Works with up to ~150,000 words input, 20,000 words output.",
    maxTokens: 128_000,
    type: "chat",
    creditUsageLevel: 5,
    disallowsTemperature: true,
  },
  "claude-fable-5-1": {
    provider: "claude",
    description: "Anthropic's strongest model for complex reasoning, planning, and instruction-following. Prose quality is comparable to Opus. • Works with up to ~750,000 words input, 95,000 words output.",
    maxTokens: 128_000,
    type: "chat",
    creditUsageLevel: 5,
    disallowsTemperature: true,
    supportsBedrockAdaptiveThinkingEffort: true,
  },
};

const availableEngineValues = Object.keys(availableEngines);
export const STORY_BIBLE_KEYS = [
  "braindump",
  "genre",
  "style",
  "synopsis",
  "characters",
  "characters_raw",
  "outline",
  "scene_summary",
  "is_story_bible_active",
  // "chapter_beats",
  "chapter_scenes",
  "chapter_scenes_extra_instructions",
  "worldbuilding",
  "worldbuilding_raw",
];

// Used to make sure we don't display deprecated variables in the UI for plugins that may have them
export const DEPRECATED_STORY_BIBLE_KEYS = ["chapter_beats"];

export const getInputStoryBibleBlock = () => {
  return {
    blockType: BLOCK_TYPES.INPUT_STORY_BIBLE,
    outputs: [
      {
        name: "story_bible",
        type: "object",
        keys: STORY_BIBLE_KEYS,
      },
    ],
    options: {},
    required: false,
  };
};

export const defaultApiInputBlock = () => {
  return {
    id: "api-input-1",
    blockType: BLOCK_TYPES.API_INPUT,
    outputs: [
      {
        name: "default_variable",
        type: "text",
      },
    ],
    options: {
      variables: [
        {
          id: "var-1",
          name: "default_variable",
          description: "default variable",
        },
      ],
    },
  };
};

export const defaultBlocks = [
  {
    id: "input-1",
    blockType: "input_document",
    outputs: [
      {
        name: "preceding_text",
        type: "text",
      },
      {
        name: "following_text",
        type: "text",
      },
      {
        name: "highlighted_text",
        type: "text",
      },
      {
        name: "previous_document_text",
        type: "text",
      },
      {
        name: "documentId",
        type: "documentId",
      },
      {
        name: "card_prompt",
        type: "text",
      },
    ],
    options: {
      outputs: [
        { source: "preceding_text", minWords: 0, maxWords: 500, target: "preceding_text" },
        { source: "following_text", minWords: 0, maxWords: 500, target: "following_text" },
        { source: "highlighted_text", minWords: 0, maxWords: 2000, target: "highlighted_text" },
        { source: "previous_document_text", minWords: 0, maxWords: 2000, target: "previous_document_text" },
        { source: "prompt_builder", target: "card_prompt" },
      ],
    },
    required: true,
  },
  { ...getInputStoryBibleBlock(), id: "input-2" },
  {
    id: "prompt-1",
    blockType: "inference",
    required: false,
    inputs: [
      {
        name: "highlighted_text",
        type: "text",
      },
      {
        name: "preceding_text",
        type: "text",
      },
      {
        name: "following_text",
        type: "text",
      },
      {
        name: "previous_document_text",
        type: "text",
      },
      ...STORY_BIBLE_KEYS.map((key) => ({
        name: key,
        type: "text",
      })),
    ],
    outputs: [
      {
        name: "generations",
        type: "list_of_texts",
      },
      {
        name: "completionId",
        type: "text",
      },
    ],
    options: {
      stream: true,
      prompt: {
        type: "chat",
        messages: [
          {
            role: "system",
            template: "You are a helpful writing assistant that will follow the directions of the user.",
          },
          {
            role: "user",
            template:
              "Here is a passage of text:\n\n{{ preceding_text}}{{ highlighted_text }}\n\nRewrite the text to add more lush descriptions that you'd find in a urban fantasy YA novel set in San Francisco. You have latitude to change the tone if necessary. Keep the rewritten text about the same length as the original.",
          },
        ],
      },
      temperature: 0.85,
      frequencyPenalty: 0,
      presencePenalty: 0.25,
      stop: "",
      numGenerations: 1,
      maxTokens: 2000,
      engine: "gpt-4o",
    },
  },
  {
    id: "output-1",
    blockType: "card_output",
    required: true,
    inputs: [
      {
        name: "card_prompt",
        type: "text",
      },
      {
        name: "content",
        type: "list_of_texts",
      },
      {
        name: "completionId",
        type: "text",
      },
    ],
    options: {},
  },
];

export const defaultConnections = [
  {
    source: "input-1",
    sourcePath: "highlighted_text",
    target: "prompt-1",
    targetPath: "highlighted_text",
  },
  {
    source: "input-1",
    sourcePath: "preceding_text",
    target: "prompt-1",
    targetPath: "preceding_text",
  },
  {
    source: "input-1",
    sourcePath: "following_text",
    target: "prompt-1",
    targetPath: "following_text",
  },
  {
    source: "input-1",
    sourcePath: "previous_document_text",
    target: "prompt-1",
    targetPath: "previous_document_text",
  },
  {
    source: "input-1",
    sourcePath: "card_prompt",
    target: "output-1",
    targetPath: "card_prompt",
  },
  ...STORY_BIBLE_KEYS.map((key) => ({
    source: "input-2",
    sourcePath: `story_bible.${key}`,
    target: "prompt-1",
    targetPath: key,
  })),
  {
    source: "prompt-1",
    sourcePath: "generations",
    target: "output-1",
    targetPath: "content",
  },
  {
    source: "prompt-1",
    sourcePath: "completionId",
    target: "output-1",
    targetPath: "completionId",
  },
];

export const defaultPlugin = {
  name: "",
  description: "",
  categoryId: "",
  type: "document",
  blocks: defaultBlocks,
  connections: defaultConnections,
  status: PluginStatus.PUBLISHED,
  promptVisibility: PluginPromptVisibility.HIDDEN,
};

export const getDefaultBasicPlugin = ({ name, description } = {}) => {
  const settings = { ...defaultPlugin };

  // allow overriding default name and description
  if (name) {
    settings.name = name;
  }

  if (description) {
    settings.description = description;
  }

  return PluginSchema.cast(settings);
};

/**
 * Enables "user_text_input" as a variable to be used in plugins. Adds:
 * - An option and output to the "input_document" block
 * - An input to the "inference" block
 * - A connection for the variable between the two blocks
 *
 * Not mutable — returns a new plugin with updated fields.
 */
export function addUserTextInputOption(plugin) {
  const inputDocumentBlock = plugin.blocks.find((block) => block.blockType === "input_document");
  const inferenceBlocks = plugin.blocks.filter((block) => block.blockType === "inference");
  const connections = Array.isArray(plugin.connections) ? plugin.connections : [];

  if (!inputDocumentBlock) {
    throw new Error("Invariant: plugin lacks a block of type input_document");
  }

  if (inferenceBlocks.length === 0) {
    throw new Error("Invariant: plugin lacks block of type inference");
  }

  return {
    ...plugin,
    blocks: plugin.blocks.map((block) => {
      if (block.blockType === "input_document") {
        return {
          ...block,
          options: {
            outputs: block.options.outputs.concat(OptionOutputSchema.cast({ source: "user_text_input" })),
          },
          outputs: block.outputs.concat({ name: "user_text_input", type: "text" }),
        };
      }

      if (block.blockType === "inference") {
        return {
          ...block,
          inputs: block.inputs.concat({ name: "user_text_input", type: "text" }),
        };
      }

      return block;
    }),
    connections: connections.concat(
      inferenceBlocks.map((inferenceBlock) => ({
        source: inputDocumentBlock.id,
        sourcePath: "user_text_input",
        target: inferenceBlock.id,
        targetPath: "user_text_input",
      }))
    ),
  };
}

/**
 * Removes the "user_text_input" variable from plugins. Reverses the attributes
 * set through addUserTextInputOption. Not mutable — returns a new plugin with
 * updated fields.
 */
export function removeUserTextInputOption(plugin) {
  const connections = Array.isArray(plugin.connections) ? plugin.connections : [];

  return {
    ...plugin,
    blocks: plugin.blocks.map((block) => {
      switch (block.blockType) {
        case "input_document":
          return {
            ...block,
            options: {
              ...block.options,
              outputs: block.options.outputs.filter((o) => o.source !== "user_text_input"),
            },
            outputs: block.outputs.filter((o) => o.name !== "user_text_input"),
          };
        case "inference":
          return {
            ...block,
            inputs: block.inputs.filter((i) => i.name !== "user_text_input"),
          };
        default:
          return block;
      }
    }),
    connections: connections.filter((c) => {
      return c.sourcePath !== "user_text_input" && c.targetPath !== "user_text_input";
    }),
  };
}

const inputOutputSchema = yup.array().of(
  yup.object().shape({
    name: yup.string().required(),
    type: yup.string().oneOf(["text", "documentId", "list_of_texts", "object"]),
    keys: yup.array().of(yup.string()),
  })
);

// Formik treats empty number inputs as strings. Coercing the empty string into
// a fallback number lets us satisfy the case where a plugin author deletes the
// value entirely, but thinks to use the default value seen in the placeholder.
const NumberWithFallback = (fallback) =>
  yup
    .number()
    .transform((value) => (isNaN(value) ? fallback : value))
    .default(fallback);

const OptionOutputSchema = yup.lazy((output) => {
  const baseSchema = yup.object({
    source: yup.string().required(),
    target: yup.string().default(output.source),
    minWords: NumberWithFallback(0).max(yup.ref("maxWords"), "Can't be more than maximum words").min(0, "Can't have a negative number of words").integer("Must be a whole number"),
    maxWords: NumberWithFallback(2000).min(0, "Can't have a negative number of words").integer("Must be a whole number"),
  });

  if (output.source === "user_text_input") {
    return baseSchema.shape({
      instructionsText: yup.string().default(""),
    });
  }

  if (output.source === "preceding_text") {
    return baseSchema.shape({
      maxWords: NumberWithFallback(500).min(0, "Can't have a negative number of words").integer("Must be a whole number"),
    });
  }

  return baseSchema;
});

const documentBlockOptionsSchema = yup.object().shape({
  outputs: yup.array().of(OptionOutputSchema).required(),
});

const defaultBlockOptions = yup.object().shape({});

const templateMustBeParseableLiquidTemplate = {
  name: "templateMustBeParseableLiquidTemplate",
  test: (value, ctx) => {
    const liquid = new Liquid();
    try {
      liquid.parse(value);
      return true;
    } catch (err) {
      return ctx.createError({ message: `Invalid Template: ${err.message}` });
    }
  },
};

export const getLiquidReferencedVariables = (template) => {
  try {
    const tokenizer = new Tokenizer(template);
    const outputTokens = tokenizer.readTopLevelTokens().filter((token) => token.kind === TokenKind.Output);
    return outputTokens.map((token) => token.content);
  } catch (err) {
    console.error("Error getting referenced variables", err);
    return [];
  }
};

export const normalizeReferencedVariable = (variable) => variable.split("|")[0].trim().split(".")[0];

const pluginUsesUserTextInputInPrompts = (plugin) => {
  const inferenceBlocks = (plugin?.blocks || []).filter((block) => block.blockType === BLOCK_TYPES.INFERENCE);

  for (const block of inferenceBlocks) {
    const messages = block?.options?.prompt?.messages || [];
    for (const message of messages) {
      if (!message?.template) continue;
      const variables = getLiquidReferencedVariables(message.template);
      const usesUserTextInput = variables.some(
        (variable) => normalizeReferencedVariable(variable) === "user_text_input"
      );
      if (usesUserTextInput) {
        return true;
      }
    }
  }

  return false;
};

const templateVariablesMustBeDeclared = {
  name: "templateVariablesMustBeDeclared",
  test: (value, ctx) => {
    try {
      const tokenizer = new Tokenizer(value);
      const outputTokens = tokenizer.readTopLevelTokens().filter((token) => token.kind === TokenKind.Output);
      const variables = outputTokens.map((token) => token.content);
      const [messagesRef, promptRef, optionsRef, blockRef, pluginRef] = ctx.from;
      const declaredInputs = blockRef.value.inputs.map((input) => input.name);
      const missingDeclarations = variables.filter((variable) => !declaredInputs.includes(variable));
      const missingNonAutoVariables = missingDeclarations.filter(
        (variable) => variable !== "user_text_input"
      );
      if (missingNonAutoVariables.length > 0) {
        return ctx.createError({
          message: `The following variables are used but not available: ${missingNonAutoVariables.join(", ")}`,
        });
      }

      if (variables.includes("chapter_beats")) {
        return ctx.createError({ message: "The {{ chapter_beats }} variable is no longer available. Please use {{ chapter_scenes }} instead." });
      }
      return true;
    } catch (err) {
      // swallow error
    }
  },
};

const maxTokensMustBeLessThanSelectedEngineMaxTokens = {
  name: "maxTokensMustBeLessThanSelectedEngineMaxTokens",
  test: (value, ctx) => {
    const [optionsRef, blockRef, pluginRef] = ctx.from;
    const engine = resolveInferenceEngine(optionsRef.value.engine);
    const engineDef = availableEngines[engine];
    if (!engineDef) {
      return ctx.createError({ message: `Selected Engine ${engine} is not available.` });
    }
    if (value > engineDef.maxTokens) {
      return ctx.createError({ message: `Selected Engine ${engine} can accept up to ${engineDef.maxTokens}.` });
    }
    return true;
  },
};

const inferenceBlockOptionsSchema = yup.object().transform(resolveInferenceOptions).shape({
  stream: yup.boolean().required(),
  prompt: yup
    .object()
    .shape({
      type: yup.string().oneOf(["chat"]),
      messages: yup.array().of(
        yup.object().shape({
          role: yup.string().oneOf(["system", "user"]),
          template: yup.string().test(templateMustBeParseableLiquidTemplate).test(templateVariablesMustBeDeclared).required(),
        })
      ),
    })
    .required(),
  temperature: yup.number().min(0, "Must be greater than 0").max(1, "Must be less than 1").required(),
  frequencyPenalty: yup.number().min(0, "Must be greater than 0").max(1, "Must be less than 1").required(),
  presencePenalty: yup.number().min(0, "Must be greater than 0").max(1, "Must be less than 1").required(),
  stop: yup.string(),
  includeReasoning: yup.boolean(),
  reasoning: yup.object().shape({
    enabled: yup.boolean(),
    exclude: yup.boolean(),
  }),
  distinctSequentialGenerations: yup.boolean(),
  maxTokens: yup.number().positive("Must be a positive number").integer("Must be a whole number").test(maxTokensMustBeLessThanSelectedEngineMaxTokens).required(),
  engine: yup.string().transform(resolveInferenceEngine).oneOf(availableEngineValues).required(),
  name: yup.string().nullable(), // Optional trace name for the generation
});

const BlockSchema = yup.object().shape({
  id: yup.string().required(),
  blockType: yup.string().oneOf(Object.values(BLOCK_TYPES)).required(),
  outputs: inputOutputSchema,
  inputs: inputOutputSchema,
  options: yup
    .object()
    .when("blockType", {
      is: BLOCK_TYPES.INPUT_DOCUMENT,
      then: () => documentBlockOptionsSchema,
    })
    .when("blockType", {
      is: BLOCK_TYPES.INFERENCE,
      then: () => inferenceBlockOptionsSchema,
    })
    .when("blockType", {
      is: "$other",
      then: () => defaultBlockOptions,
    }),
});

const connectionSchema = yup.object().shape({
  source: yup.string().required(),
  sourcePath: yup.string().required(),
  target: yup.string().required(),
  targetPath: yup.string().required(),
});

export const blocksSchema = yup.array().of(BlockSchema);
export const connectionsSchema = yup.array().of(connectionSchema);

// Validation used on the Describe Plugin step
export const PluginSchema = yup.object().shape({
  description: yup.string().min(MIN_DESCRIPTION_LENGTH, `Description must be at least ${MIN_DESCRIPTION_LENGTH} characters`).required("Description is required"),
});

// Full validation used on the Save/Publish step
export const PluginFullSchema = yup
  .object()
  .shape({
    name: yup.string().min(MIN_NAME_LENGTH, `Name must be at least ${MIN_NAME_LENGTH} character`).required("Name is required"),
    description: yup.string().min(MIN_DESCRIPTION_LENGTH, `Description must be at least ${MIN_DESCRIPTION_LENGTH} characters`).required("Description is required"),
    categoryId: yup.string().required("Category is required"),
    status: yup.string().oneOf(Object.values(PluginStatus)).required("Status is required"),
    promptVisibility: yup.string().oneOf(Object.values(PluginPromptVisibility)).required("Prompt visibility is required"),
    type: yup.string().oneOf(TYPES).required(),
    blocks: blocksSchema.required(),
    connections: connectionsSchema.required(),
  })
  .test({
    name: "userTextInputRequiresCustomizedInstructions",
    test(plugin) {
      if (!pluginUsesUserTextInputInPrompts(plugin)) {
        return true;
      }

      const inputDocumentBlock = (plugin?.blocks || []).find(
        (block) => block.blockType === BLOCK_TYPES.INPUT_DOCUMENT
      );
      const userTextOption = inputDocumentBlock?.options?.outputs?.find(
        (output) => output.source === "user_text_input"
      );
      const instructionsText = String(userTextOption?.instructionsText || "").trim();

      if (instructionsText.length > 0) {
        return true;
      }

      const inputDocumentBlockIndex = (plugin?.blocks || []).findIndex(
        (block) => block.blockType === BLOCK_TYPES.INPUT_DOCUMENT
      );
      const userTextOptionIndex = inputDocumentBlock?.options?.outputs?.findIndex(
        (output) => output.source === "user_text_input"
      );
      const path = inputDocumentBlockIndex >= 0 && userTextOptionIndex >= 0
        ? `blocks[${inputDocumentBlockIndex}].options.outputs[${userTextOptionIndex}].instructionsText`
        : "blocks";

      return this.createError({
        path,
        message:
          "Add custom popup instructions for User Instructions before saving your plugin.",
      });
    },
  });

// Schema for inference block's messages
const basicInferenceMessagesSchema = yup.object({
  role: yup.string().oneOf(["system", "user"]).required(),
  template: yup.string().required(),
});

// Schema for the specific form of the inference block's options
const basicSpecificInferenceOptionsSchema = yup.object({
  type: yup.string().required().equals(["chat"]),
  messages: yup
    .array()
    .of(basicInferenceMessagesSchema)
    .required()
    .test("message-order", "Messages must be in specific order: [system, user]", function (value) {
      if (!Array.isArray(value) || value.length !== 2) {
        console.log("failed array test");
        return false;
      }

      // Check message roles are in the correct order
      if (value[0].role !== "system" || value[1].role !== "user") {
        console.log("failed role test");
        return false;
      }

      const [passed, _] = checkBasicTemplate(value[1].template);

      return passed;
    }),
});

export const checkBasicTemplate = (template) => {
  const lines = template.split("\n").map((line) => line.trim());

  const variables = {
    braindump: {
      descriptor: "Here is a braindump of the story:",
      variable: "{{ braindump }}",
    },
    genre: {
      descriptor: "The genre of the story is:",
      variable: "{{ genre }}",
    },
    style: {
      descriptor: "The style of the story is:",
      variable: "{{ style }}",
    },
    synopsis: {
      descriptor: "Here is a synopsis of the story:",
      variable: "{{ synopsis }}",
    },
    characters: {
      descriptor: "Here are the characters of the story:",
      variable: "{{ characters }}",
    },
    outline: {
      descriptor: "Here is the outline of the story:",
      variable: "{{ outline }}",
    },
    worldbuilding: {
      descriptor: "Here are the the world building entries:",
      variable: "{{ worldbuilding }}",
    },
  };

  let index = 0;
  const storyEngineVariables = {
    braindump: false,
    genre: false,
    style: false,
    synopsis: false,
    characters: false,
    outline: false,
    worldbuilding: false,
  };

  // Function to skip empty lines and return next non-empty line index
  const skipEmptyLines = () => {
    while (index < lines.length && lines[index] === "") {
      // console.log("index skip", index);
      index++;
    }
    return index;
  };

  skipEmptyLines();

  // Handle the mandatory section
  if (lines[index] !== "Here is a passage of text:") {
    console.log("Missing highlighted_text section descriptor");
    return [false, null];
  }
  index++;
  skipEmptyLines();

  let cleaned = lines[index].replace(/\s+/g, "");

  // Patterns to check
  let precedingPattern = /\{\{preceding_text\}\}/;
  let highlightedPattern = /\{\{highlighted_text\}\}/;

  // Check patterns
  let hasPreceding = precedingPattern.test(cleaned);
  let hasHighlighted = highlightedPattern.test(cleaned);

  // Check highlighted or preceding text section
  if (!hasPreceding || !hasHighlighted) {
    console.log("Missing or misformatted highlighted_text and preceding_text section");
    return [false, null];
  }
  index++;

  // console.log("passed highlighted_text section");

  // Handle the optional sections
  while (index < lines.length) {
    // console.log("index", index);
    // console.log(lines[index]);
    // let foundSection = false;
    skipEmptyLines();

    for (let variable in variables) {
      const info = variables[variable];

      if (lines[index] === info.descriptor) {
        // console.log("descriptor found!", info.descriptor);
        index++;
        skipEmptyLines();
        if (lines[index] !== info.variable) {
          console.log("Mismatched section");
          return [false, null];
        }

        storyEngineVariables[variable] = true;
        index++;
        // foundSection = true;
        break;
      }
    }

    // if (!foundSection) {
    index++;
    // }
  }

  // console.log("passed!");
  return [true, storyEngineVariables];
};

/**
 * Given blocks, this will extract the basic prompt, story bible varibles, and system
 * @param {Array} blocks - The blocks to extract from
 * @returns {Array} An array containing the [prompt, variables, system]
 */
export const extractBasicPrompt = (blocks) => {
  // find the first inference block
  const idx = blocks.findIndex((block) => block.blockType === "inference");
  const block = blocks[idx];

  const template = block.options.prompt.messages[1].template;
  const system = block.options.prompt.messages[0].template;

  const [passed, variables] = checkBasicTemplate(template);

  if (!passed) {
    throw new Error("Cannot extract from invalid template");
  }

  // Split the template into lines
  const lines = template.split("\n");

  // Find the index of the last template variable
  let lastTemplateIndex = -1;
  lines.forEach((line, index) => {
    line = line.trim();
    if (line.startsWith("{{") && line.endsWith("}}")) {
      lastTemplateIndex = index;
    }
  });

  // Join all lines that appear after the last template variable
  const prompt = lines
    .slice(lastTemplateIndex + 1)
    .join("\n")
    .trim();

  return [prompt, variables, system];
};

// Generate the inference block's messages prompt and engine from the basic editor's form fields
export const generateBasicInferenceParams = ({ basicPrompt, storyBibleVariables, system }) => {
  let inferenceTemplate = `Here is a passage of text:\n\n{{ preceding_text }}{{ highlighted_text }}`;
  let engine = "gpt-4o";
  let engineInfo = availableEngines[engine];

  const variableMetadata = {
    braindump: { descriptor: "Here is a braindump of the story", maxWords: MAX_BRAINDUMP_WORDS },
    genre: { descriptor: "The genre of the story is", maxWords: 40 },
    style: { descriptor: "The style of the story is", maxWords: 40 },
    synopsis: { descriptor: "Here is a synopsis of the story", maxWords: MAX_SYNOPSIS_WORDS },
    characters: { descriptor: "Here are the characters of the story", maxWords: 700 },
    outline: { descriptor: "Here is the outline of the story", maxWords: 1700 },
    worldbuilding: { descriptor: "Here are the the world building entries", maxWords: 700 },
  };

  const MAX_GPT_TOKENS = 4097;

  let tokens = 0;

  Object.keys(storyBibleVariables).forEach((key) => {
    const meta = variableMetadata[key];
    if (storyBibleVariables[key]) {
      inferenceTemplate += `\n\n${meta.descriptor}:\n\n{{ ${key} }}`;
      tokens += meta.maxWords;
    }
  });

  if (tokens > engineInfo.maxInputTokens) {
    // switch engine
    console.log("switching engine to claude sonnet 4.6");
    engine = "claude-sonnet-4-6";
    engineInfo = availableEngines[engine];
  }

  inferenceTemplate += `\n\n${basicPrompt}`;

  const inference = {
    type: "chat",
    messages: [
      {
        role: "system",
        template: system || "You are a helpful writing assistant that will follow the directions of the user.",
      },
      {
        role: "user",
        template: inferenceTemplate,
        basicPrompt: basicPrompt,
      },
    ],
  };

  return [inference, engine];
};

// The specific blocks schema with the custom validation
const basicBlocksSchema = yup
  .array()
  .of(BlockSchema)
  .test("block-order", "Blocks must be in specific order: [input_document, inference, card_output]", async function (value) {
    if (!Array.isArray(value) || (value.length !== 3 && value.length !== 4)) {
      console.log("failed array test", value);
      return false;
    }

    let storyBibleBlock, inputBlock, inferenceBlock, outputBlock;

    // Check block types are in the correct order. this requires SB now
    if (value.length === 4) {
      inputBlock = value[0];
      storyBibleBlock = value[1];
      inferenceBlock = value[2];
      outputBlock = value[3];

      // story bible
      if (inputBlock.blockType !== "input_document" && storyBibleBlock.blockType !== "story_bible" && inferenceBlock.blockType !== "inference" && outputBlock.blockType !== "card_output") {
        console.log("failed story bible block type test");
        return false;
      }

      if (inferenceBlock.options.engine !== "gpt-3.5-turbo-16k" && inferenceBlock.options.engine !== "gpt-3.5-turbo" && inferenceBlock.options.engine !== "gpt-4o") {
        console.log("failed engine test");
        return false;
      }

      if (
        inferenceBlock.options.temperature != 0.85 ||
        inferenceBlock.options.frequencyPenalty != 0 ||
        inferenceBlock.options.presencePenalty != 0.25 ||
        inferenceBlock.options.maxTokens != 2000 ||
        inferenceBlock.options.numGenerations != 1 ||
        inferenceBlock.options.stop != ""
      ) {
        console.log("failed inference options test");
        return false;
      }
    } else {
      console.log("failed length test", value.length);
      return false;
    }

    // Check the specific form of the inference block's options
    try {
      await basicSpecificInferenceOptionsSchema.validate(inferenceBlock.options.prompt);
      return true;
    } catch (e) {
      console.log("Inference block options are invalid: " + JSON.stringify(inferenceBlock.options.prompt));
      return this.createError({
        path: "options",
        message: "Inference block options are invalid: " + JSON.stringify(inferenceBlock.options.prompt),
      });
    }
  });

export const BasicPluginSchema = yup.object().shape({
  type: yup.string().oneOf(TYPES),
  blocks: basicBlocksSchema,
  connections: connectionsSchema,
});
